import React, { useState, useEffect } from "react";
import UserApiClient from "../../services/UserApiClient";
import AddEditUser from "./AddEditUser";
import ResetPassword from "./ResetPassword";

interface User {
  id: string;
  userName: string;
  firstName: string;
  lastName: string;
  role: string;
  email: string;
  phone: string;
  isActive: boolean;
  password?: string; // Optional since it's not needed for display
}

interface UserListProps {
  businessId: string;
}

const UserList: React.FC<UserListProps> = ({ businessId }) => {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>("");
  const [isAddEditUserOpen, setIsAddEditUserOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [isResetPasswordOpen, setIsResetPasswordOpen] = useState(false);
  const [selectedUserForReset, setSelectedUserForReset] = useState<User | null>(
    null
  );

  useEffect(() => {
    if (businessId) {
      fetchUsers(businessId);
    } else {
      setUsers([]);
      setError("");
    }
  }, [businessId]);

  const fetchUsers = async (currentBusinessId: string) => {
    setLoading(true);
    setError("");
    try {
      const userData = await UserApiClient.fetchUsers(currentBusinessId);
      // Map the full API response to our simplified User interface
      const simplifiedUsers = userData.map((apiUser: any) => ({
        id: apiUser.id,
        userName: apiUser.userName,
        firstName: apiUser.firstName,
        lastName: apiUser.lastName,
        email: apiUser.email,
        phone: apiUser.phone,
        role: apiUser.role,
        isActive: apiUser.isActive,
      }));
      setUsers(simplifiedUsers);
    } catch (error: any) {
      console.error("Error fetching users:", error);
      setError("Failed to load users");
      setUsers([]);
    } finally {
      setLoading(false);
    }
  };

  const handleAddUser = () => {
    setSelectedUser(null); // null indicates we're adding, not editing
    setIsAddEditUserOpen(true);
  };

  const handleEditUser = (user: User) => {
    setSelectedUser(user);
    setIsAddEditUserOpen(true);
  };

  const handleCloseAddEditUser = () => {
    setIsAddEditUserOpen(false);
    setSelectedUser(null);
  };

  const handleResetPassword = (user: User) => {
    setSelectedUserForReset(user);
    setIsResetPasswordOpen(true);
  };

  const handleCloseResetPassword = () => {
    setIsResetPasswordOpen(false);
    setSelectedUserForReset(null);
  };

  const handleSaveUser = () => {
    // Refresh the user list after saving
    if (businessId) {
      fetchUsers(businessId);
    }
    handleCloseAddEditUser();
  };

  const handlePasswordReset = () => {
    // Refresh the user list after password reset (optional)
    if (businessId) {
      fetchUsers(businessId);
    }
    handleCloseResetPassword();
  };

  const handleRefresh = () => {
    if (businessId) {
      fetchUsers(businessId);
    }
  };

  if (!businessId) {
    return (
      <div className="user-list-container">
        <div className="user-list-header">
          <h3>👥 Users</h3>
        </div>
        <p className="terminal-list-message">
          Please select a business to view users
        </p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="user-list-container">
        <div className="user-list-header">
          <h3>👥 Users</h3>
        </div>
        <div className="user-list-loading">Loading users...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="user-list-container">
        <div className="user-list-header">
          <h3>👥 Users</h3>
        </div>
        <div className="user-list-error">{error}</div>
      </div>
    );
  }

  if (!users || users.length === 0) {
    return (
      <div className="user-list-container">
        <div className="user-list-header">
          <h3>👥 Users</h3>
          <div style={{ display: "flex", gap: "0.5rem" }}>
            <button
              className="wide-dash-border-btn"
              onClick={handleRefresh}
              title="Refresh Users"
              disabled={loading}
            >
              ↻ Refresh
            </button>
            <button
              className="wide-dash-border-btn"
              onClick={handleAddUser}
              title="Add User"
            >
              ➕ Add User
            </button>
          </div>
        </div>
        <p className="terminal-list-message">
          No users found for this business
        </p>

        {isAddEditUserOpen && (
          <AddEditUser
            isOpen={isAddEditUserOpen}
            user={selectedUser}
            businessId={businessId}
            onClose={handleCloseAddEditUser}
            onSave={handleSaveUser}
          />
        )}
      </div>
    );
  }

  return (
    <div className="user-list-container">
      <div className="user-list-header">
        <h3>👥 Users ({users.length})</h3>
        <div style={{ display: "flex", gap: "0.5rem" }}>
          <button
            className="wide-dash-border-btn"
            onClick={handleRefresh}
            title="Refresh Users"
            disabled={loading}
          >
            ↻ Refresh
          </button>
          <button
            className="wide-dash-border-btn"
            onClick={handleAddUser}
            title="Add User"
          >
            ➕ Add User
          </button>
        </div>
      </div>

      <div className="table-container">
        <table className="payment-history-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Username</th>
              <th>Role</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {users.map((user) => (
              <tr key={user.id}>
                <td>
                  <div
                    className="user-name-container"
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "0.5rem",
                    }}
                  >
                    <span className="user-icon">👤</span>
                    <span className="user-full-name">
                      {user.firstName} {user.lastName}
                    </span>
                  </div>
                </td>
                <td>{user.userName}</td>
                <td>
                  <span
                    className="user-role"
                    style={{
                      padding: "0.25rem 0.5rem",
                      borderRadius: "12px",
                      fontSize: "0.75rem",
                      fontWeight: "500",
                      background: "#e0e7ff",
                      color: "#3730a3",
                      border: "1px solid #c7d2fe",
                    }}
                  >
                    {user.role}
                  </span>
                </td>
                <td>
                  <span
                    className={`terminal-status-badge ${
                      user.isActive ? "status-active" : "status-inactive"
                    }`}
                  >
                    {user.isActive ? "Active" : "Inactive"}
                  </span>
                </td>
                <td style={{ textAlign: "right" }}>
                  <div
                    style={{
                      display: "flex",
                      gap: "0.5rem",
                      justifyContent: "flex-end",
                    }}
                  >
                    <button
                      className="btn-icon btn-edit-icon"
                      onClick={() => handleEditUser(user)}
                      title="Edit User"
                    >
                      ✏️
                    </button>
                    <button
                      className="btn-icon"
                      onClick={() => handleResetPassword(user)}
                      title="Reset Password"
                      style={{
                        background: "#fef3c7",
                        borderColor: "#f59e0b",
                        color: "#92400e",
                      }}
                    >
                      🔑
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {isAddEditUserOpen && (
        <AddEditUser
          isOpen={isAddEditUserOpen}
          user={selectedUser}
          businessId={businessId}
          onClose={handleCloseAddEditUser}
          onSave={handleSaveUser}
        />
      )}

      {isResetPasswordOpen && (
        <ResetPassword
          isOpen={isResetPasswordOpen}
          user={selectedUserForReset}
          businessId={businessId}
          onClose={handleCloseResetPassword}
          onSave={handlePasswordReset}
        />
      )}
    </div>
  );
};

export default UserList;
